/* eslint-disable @typescript-eslint/no-explicit-any */
import { Center, Spinner, Text } from "@chakra-ui/react";
import { ReactNode, useEffect, useRef } from "react";
import { useInfiniteQuery } from "react-query";

export const getInfiniteQuery = (
  dependencyArray: any[],
  fetchFunction: any,
  fetchParameters: any
) => {
  const infiniteQuery = useInfiniteQuery(
    dependencyArray,
    ({ pageParam = 1 }) =>
      fetchFunction({ ...fetchParameters, page: pageParam }),
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage?.hasNext) {
          return null;
        }
        return lastPage.nextCursor;
      },
    }
  );

  return infiniteQuery;
};

export const getInfinityPages = (
  infiniteQuery: any,
  indicator: string | null = "data"
): any[] => {
  const flattenedPages: any[] = [];
  // console.log(infiniteQuery);

  infiniteQuery?.data?.pages?.forEach((page: any) => {
    const list = (indicator != null ? page?.[indicator] : page) ?? [];
    list.forEach((item: any) => {
      flattenedPages.push(item);
    });
  });

  return flattenedPages;
};

const BottomInfinityLoader = ({
  bottomRef,
  isFetchingNextPage,
  hasNextPage,
  isLoading,
}: {
  bottomRef: React.RefObject<HTMLDivElement>;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  isLoading: boolean;
}) => {
  return (
    <>
     
        {isLoading ? (
          <></>
        ) : isFetchingNextPage ? (
           <Center w="100%" h="60px" gap="10px" alignItems="center">
            <Spinner size="sm" color="#171C22" />
            <Text fontSize="lg" color="#171C22">
              Loading more
            </Text>
            </Center>
        ) : hasNextPage ? (
          <Center w="100%" h="60px" gap="10px" alignItems="center">
          <Text ref={bottomRef} fontSize="lg" color="#171C22">
            Scroll down to see more
          </Text>
          </Center>
        ) : (
          <Text fontSize="lg" color="#171C22"></Text>
        )}
      
    </>
  );
};

const RightInfinityLoader = ({
  rightRef,
  isFetchingNextPage,
  hasNextPage,
  isLoading,
}: {
  rightRef: React.RefObject<HTMLDivElement>;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  isLoading: boolean;
}) => {
  return (
    <>
      <Center w="200px" gap="10px" marginLeft="auto">
        {isLoading ? (
          <></>
        ) : isFetchingNextPage ? (
          <>
            <Center flexDir="column">
              <Spinner size="lg" color="#171C22" />
              <Text fontSize="lg" color="#171C22">
                Loading more
              </Text>
            </Center>
          </>
        ) : hasNextPage ? (
          <Text ref={rightRef} fontSize="lg" color="#171C22">
            Scroll right to see more
          </Text>
        ) : (
          <Text fontSize="lg" color="#171C22"></Text>
        )}
      </Center>
    </>
  );
};

export const InfinityList = ({
  children,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  hide,
}: {
  children: ReactNode;
  onLoadMore: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  hide?: boolean;
}) => {
  const rightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 1,
    };

    const callback = (entries: any) => {
      entries.forEach((entry: any) => {
        if (entry.isIntersecting && hasNextPage) {
          onLoadMore();
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);

    if (rightRef.current) {
      observer.observe(rightRef.current);
    }

    return () => {
      if (rightRef.current) {
        observer.unobserve(rightRef.current);
      }
    };
  }, [hasNextPage, isFetchingNextPage]);

  return (
    <>
      {children}

      {!hide && (
        <RightInfinityLoader
          rightRef={rightRef}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

const InfinityGrid = ({
  children,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  hide,
}: {
  children: ReactNode;
  onLoadMore: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  hide?: boolean;
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 1,
    };

    const callback = (entries: any) => {
      entries.forEach((entry: any) => {
        if (entry.isIntersecting && hasNextPage) {
          onLoadMore();
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);

    if (bottomRef.current) {
      observer.observe(bottomRef.current);
    }

    return () => {
      if (bottomRef.current) {
        observer.unobserve(bottomRef.current);
      }
    };
  }, [hasNextPage, isFetchingNextPage]);

  return (
    <>
      {children}

      {!hide && (
        <BottomInfinityLoader
          bottomRef={bottomRef}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default InfinityGrid;
